import React from 'react';
import { formatBackendEnumLabel, formatPlayerRoleLabel } from '../../../../helper/helper';
import type { IPlayerByLeagueForRequest } from '../types/request';

interface AllPlayersListViewProps {
  players: IPlayerByLeagueForRequest[];
  onAddRole?: (player: IPlayerByLeagueForRequest) => void;
  onMerge?: (player: IPlayerByLeagueForRequest) => void;
  mergingPlayerId?: string | null;
}

const getInitials = (firstName?: string, lastName?: string): string => {
  const f = (firstName ?? '').trim().charAt(0).toUpperCase();
  const l = (lastName ?? '').trim().charAt(0).toUpperCase();
  if (f && l) return `${f}${l}`;
  if (f) return f;
  if (l) return l;
  return '-';
};

export const AllPlayersListView: React.FC<AllPlayersListViewProps> = ({
  players,
  onAddRole,
  onMerge,
  mergingPlayerId,
}) => {
  return (
    <div className="all-players-list-table-wrap">
      <table className="all-players-list-table">
        <thead>
          <tr>
            <th>PLAYER</th>
            <th>TEAM ID</th>
            <th>ROLE</th>
            <th>BATTING STYLE</th>
            <th>BOWLING STYLE</th>
            <th>ACTION</th>
          </tr>
        </thead>
        <tbody>
          {players.map((player) => {
            const fullName =
              [player.playerFirstName, player.playerLastName].filter(Boolean).join(' ') || '-';
            const teamId = player.teamCode ?? player.teamSnapshotId ?? player.playerId ?? '';
            const role = formatPlayerRoleLabel(player.playerRole ?? '-');
            const batting = formatBackendEnumLabel(player.battingStyle ?? '-');
            const bowling = formatBackendEnumLabel(player.bowlingStyle ?? '-');
            const imageUri = player.imageUri;
            const cdnBaseUrl = import.meta.env.VITE_IMAGE_CDN_BASE_URL;
            const avatarSrc =
              imageUri && cdnBaseUrl
                ? `${cdnBaseUrl.replace(/\/$/, '')}/${imageUri.replace(/^\//, '')}`
                : null;
            const initials = getInitials(player.playerFirstName, player.playerLastName);
            const showMerge = player.userStatus === 'UNCLAIMED' || player.userStatus === 'IN_CLAIM';
            const canAddRole = !showMerge;
            const rowMerging = !!player.playerId && mergingPlayerId === player.playerId;
            const mergeDisabled = player.userStatus === 'IN_CLAIM' || rowMerging;

            return (
              <tr key={player.playerId ?? `${fullName}-${teamId}`}>
                <td>
                  <div className="all-players-list-player-cell">
                    <div className="all-players-list-avatar">
                      {avatarSrc ? (
                        <img src={avatarSrc} alt="" />
                      ) : (
                        <span className="all-players-list-avatar-initials">{initials}</span>
                      )}
                    </div>
                    <span className="all-players-list-name">{fullName}</span>
                  </div>
                </td>
                <td>
                  <span className="all-players-list-team-id">{teamId}</span>
                </td>
                <td>
                  <span className="all-players-list-value">{role}</span>
                </td>
                <td>
                  <span className="all-players-list-value">{batting}</span>
                </td>
                <td>
                  <span className="all-players-list-value">{bowling}</span>
                </td>
                <td>
                  <div className="all-players-list-actions">
                    {canAddRole ? (
                      <button
                        type="button"
                        className="all-players-list-btn-primary"
                        onClick={() => onAddRole?.(player)}
                      >
                        Add Role
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="all-players-list-btn-outline"
                        onClick={() => onMerge?.(player)}
                        disabled={mergeDisabled}
                      >
                        {player.userStatus === 'IN_CLAIM' ? 'Pending' : rowMerging ? 'Merging...' : 'Merge'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
